import React, { useState, useEffect } from "react";
import { ChatSelectMenu } from "./ChatSelectMenu";
import { ChatMessageForm } from "./ChatMessageForm";
import {
  useCreateChatMutation,
  useDeleteChatMutation,
} from "../../../api/chatApi";
import type { Chat, ChatSummary, Message } from "./types";

type ChatLayoutProps = {
  chats: string;
};

export const ChatLayout: React.FC<ChatLayoutProps> = ({ chats: chatList }) => {
  const getInitialChatId = (): number | null => {
    const parsedChats = JSON.parse(chatList || "[]");
    const urlParams = new URLSearchParams(window.location.search);
    const chatParam = parseInt(urlParams.get("id"));
    if (chatParam && parsedChats.find((c: Chat) => c.id === chatParam)) {
      return chatParam;
    }
    return parsedChats.length > 0 ? parsedChats[0].id : null;
  };

  const [createChatRequest, { isLoading }] = useCreateChatMutation();
  const [deleteChatRequest] = useDeleteChatMutation();
  const [chats, setChats] = useState<Chat[]>(JSON.parse(chatList || "[]"));
  const [activeChatId, setActiveChatId] = useState<number | null>(
    getInitialChatId()
  );

  const activeChat = chats.find((c) => c.id === activeChatId) || null;

  useEffect(() => {
    if (activeChatId) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("id", activeChatId.toString());
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams}`
      );
    }
  }, [activeChatId]);

  const handleChatSelect = (chatId: number): void => {
    setActiveChatId(chatId);
  };

  const handleChatCreate = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    try {
      const response = await createChatRequest({
        name: "New chat",
      }).unwrap();

      setChats([
        {
          id: response.id,
          name: response.name,
          modelName: response.model_name,
          messages: [],
        },
        ...chats,
      ]);
      setActiveChatId(response.id);
    } catch (error) {
      console.error(error);
    }
  };

  const handleChatDelete = async (chatId: number): Promise<void> => {
    if (isLoading) {
      return;
    }

    try {
      await deleteChatRequest({
        chat_id: chatId,
      });

      setChats((l) => l.filter((item) => item.id != chatId));
    } catch (error) {
      console.error(error);
    }
  };

  const handleMessageAdded = (newMessage: Message): void => {
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat.id === activeChatId
          ? { ...chat, messages: [...chat.messages, newMessage] }
          : chat
      )
    );
  };

  return (
    <div className="tw-container tw-min-h-[50vh] tw-w-[45vw] tw-mx-auto">
      <h1 className="tw-mx-0 tw-my-10 tw-flex tw-items-center tw-text-2xl">
        <i className="material-icons tw-leading-[1]">chat_bubble</i>
        <span className="tw-ml-1">GPT chat</span>
      </h1>
      <div className="tw-flex tw-gap-12">
        <div className="tw-w-64">
          <ChatSelectMenu
            summaries={chats.map((chat) => chat as ChatSummary)}
            onChatSelect={handleChatSelect}
            onChatCreate={handleChatCreate}
            onChatDelete={handleChatDelete}
            activeChatId={activeChatId || undefined}
          />
        </div>
        <div className="tw-flex-1">
          {activeChat ? (
            <>
              <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
                <h2 className="tw-text-2xl tw-mb-4">
                  {activeChat.name || `New Chat`}
                </h2>
                <span>ID#{activeChatId}</span>
              </div>
              <ChatMessageForm
                messages={activeChat.messages}
                chatId={activeChat.id}
                onMessageAdded={handleMessageAdded}
              />
            </>
          ) : (
            <div>
              <p>No chat selected. Please select a chat or create a new one.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
